import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import {
  ELECTRICITY_CATEGORY_CONSUMPTION,
  type ElectricityCategory,
} from '@popety-io/types'
import { createElectricityT } from '../../../utils/electricityTranslations'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import { Box } from '../../atoms/Box'
import { Icon } from '../../atoms/Icon'
import { AnalysisTabButtonMenu } from '../../molecules/AnalysisTabButtonMenu'
import {
  ElectricityContainer,
  ContentContainer,
  LoadingContainer,
  ErrorContainer,
  FilterSection,
  FilterBar,
  LocationFilters,
  LocationBadge,
  CategorySelectorWrapper,
  CategoryTrigger,
  TriggerChevron,
  PanelOverlay,
  PanelContainer,
  PanelColumn,
  ColumnSeparator,
  ColumnHeader,
  ColumnHeaderIcon,
  ColumnHeaderText,
  ColumnHeaderTitle,
  ColumnHeaderSubtitle,
  ItemRow,
  ItemIcon,
  ItemTextContainer,
  ItemCode,
  ItemLabel,
  KwhBadge,
  CheckmarkBadge,
} from './ElectricityContent.styles'

const RESIDENTIAL_CATEGORIES: ElectricityCategory[] = [
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'H7',
  'H8',
]

const COMMERCIAL_CATEGORIES: ElectricityCategory[] = [
  'C1',
  'C2',
  'C3',
  'C4',
  'C5',
  'C6',
  'C7',
]

type IconName = 'building' | 'home' | 'flame' | 'zap' | 'store' | 'factory'

const ICON_MAP: Record<ElectricityCategory, IconName> = {
  H1: 'building',
  H2: 'building',
  H3: 'building',
  H4: 'building',
  H5: 'building',
  H6: 'building',
  H7: 'building',
  H8: 'building',
  C1: 'store',
  C2: 'store',
  C3: 'factory',
  C4: 'factory',
  C5: 'factory',
  C6: 'factory',
  C7: 'factory',
}

// Gradient tiers: low / mid / high consumption
type GradientTier =
  | 'res-low'
  | 'res-mid'
  | 'res-high'
  | 'com-low'
  | 'com-mid'
  | 'com-high'

const GRADIENT_TIER: Record<ElectricityCategory, GradientTier> = {
  H1: 'res-low',
  H2: 'res-low',
  H3: 'res-low',
  H4: 'res-low',
  H5: 'res-mid',
  H6: 'res-high',
  H7: 'res-mid',
  H8: 'res-low',
  C1: 'com-low',
  C2: 'com-low',
  C3: 'com-mid',
  C4: 'com-mid',
  C5: 'com-high',
  C6: 'com-high',
  C7: 'com-high',
}

// Option B: Cyan Energy Spectrum gradients
const BADGE_GRADIENTS: Record<
  GradientTier,
  {
    bg: string
    text: string
  }
> = {
  'res-low': {
    bg: 'linear-gradient(90deg, #cffafe, #67e8f9)',
    text: '#0e7490',
  },
  'res-mid': {
    bg: 'linear-gradient(90deg, #67e8f9, #22d3ee)',
    text: '#0e7490',
  },
  'res-high': {
    bg: 'linear-gradient(90deg, #22d3ee, #06b6d4)',
    text: '#ffffff',
  },
  'com-low': {
    bg: 'linear-gradient(90deg, #ffedd5, #fdba74)',
    text: '#c2410c',
  },
  'com-mid': {
    bg: 'linear-gradient(90deg, #fdba74, #fb923c)',
    text: '#9a3412',
  },
  'com-high': {
    bg: 'linear-gradient(90deg, #fb923c, #ea580c)',
    text: '#ffffff',
  },
}

const ICON_BG_KEY: Record<
  ElectricityCategory,
  'primaryLight' | 'warningLight'
> = {
  H1: 'primaryLight',
  H2: 'primaryLight',
  H3: 'primaryLight',
  H4: 'primaryLight',
  H5: 'primaryLight',
  H6: 'primaryLight',
  H7: 'primaryLight',
  H8: 'primaryLight',
  C1: 'warningLight',
  C2: 'warningLight',
  C3: 'warningLight',
  C4: 'warningLight',
  C5: 'warningLight',
  C6: 'warningLight',
  C7: 'warningLight',
}

const formatKwh = (kwh: number): string => {
  if (kwh >= 1000000) {
    const millions = kwh / 1000000
    const formatted =
      millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)
    return `${formatted}M kWh`
  }
  return new Intl.NumberFormat('de-CH').format(kwh) + ' kWh'
}

export interface ElectricityContentProps {
  data: any
  loading: boolean
  error: string | null
  activeTab: string
  onTabChange: (tab: string) => void
  selectedCategory: ElectricityCategory
  onCategoryChange: (category: ElectricityCategory) => void
  renderTabContent: () => React.ReactNode
  locationName?: string
  cantonName?: string
  readOnly?: boolean
}

export const ElectricityContent: React.FC<ElectricityContentProps> = ({
  data,
  loading,
  error,
  activeTab,
  onTabChange,
  selectedCategory,
  onCategoryChange,
  renderTabContent,
  locationName,
  cantonName,
  readOnly = false,
}) => {
  const { t: rawT, i18n } = useTranslation()
  const t = createElectricityT(rawT, i18n)
  const theme = useTheme()
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const electricityTabs = useMemo(
    () => [
      { id: 'overview', label: t('tabs.overview'), icon: 'bar-chart-2' },
      { id: 'trends', label: t('tabs.trends'), icon: 'trending-up' },
    ],
    [t]
  )

  const handleCategorySelect = useCallback(
    (category: ElectricityCategory) => {
      onCategoryChange(category)
      setIsPanelOpen(false)
    },
    [onCategoryChange]
  )

  useEffect(() => {
    if (!isPanelOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsPanelOpen(false)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isPanelOpen])

  const getBadgeColors = (category: ElectricityCategory) => {
    const tier = GRADIENT_TIER[category]
    return BADGE_GRADIENTS[tier]
  }

  const selectedConsumption = ELECTRICITY_CATEGORY_CONSUMPTION[selectedCategory]
  const selectedBadge = getBadgeColors(selectedCategory)
  const selectedIconBg = theme.colors[ICON_BG_KEY[selectedCategory]]

  const renderCategoryItem = (category: ElectricityCategory) => {
    const isSelected = category === selectedCategory
    const consumption = ELECTRICITY_CATEGORY_CONSUMPTION[category]
    const badgeColors = getBadgeColors(category)
    const iconBgKey = ICON_BG_KEY[category]
    const iconBg = theme.colors[iconBgKey]
    const description = t(`categoryDescriptions.${category}`)

    return (
      <ItemRow
        key={category}
        $selected={isSelected}
        onClick={() => handleCategorySelect(category)}
        title={description}
        type="button"
      >
        <ItemIcon $bgColor={iconBg}>
          <Icon name={ICON_MAP[category]} size="small" />
        </ItemIcon>
        <ItemTextContainer>
          <ItemCode $selected={isSelected}>{category}</ItemCode>
          <ItemLabel $selected={isSelected}>
            {t(`categories.${category}`)}
          </ItemLabel>
        </ItemTextContainer>
        <KwhBadge
          $textColor={isSelected ? '#fff' : badgeColors.text}
          $bgColor={isSelected ? theme.colors.primary : badgeColors.bg}
        >
          {formatKwh(consumption)}
        </KwhBadge>
        {isSelected && (
          <CheckmarkBadge>
            <Icon name="check" size="small" color="#fff" />
          </CheckmarkBadge>
        )}
      </ItemRow>
    )
  }

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <Box>{t('states.loading')}</Box>
        </LoadingContainer>
      )
    }

    if (error) {
      return (
        <ErrorContainer>
          <Box>{error}</Box>
        </ErrorContainer>
      )
    }

    if (!data) {
      return (
        <ErrorContainer>
          <Box>{t('states.noData')}</Box>
        </ErrorContainer>
      )
    }

    return renderTabContent()
  }

  return (
    <ElectricityContainer>
      <AnalysisSectionHeader title={t('title')} subtitle={t('subtitle')} />

      {/* Filter Section with Category Selector */}
      <FilterSection>
        <FilterBar>
          <LocationFilters>
            {/* Location Badge */}
            {locationName && (
              <LocationBadge>
                <Icon name="map-pin" size="small" />
                {locationName}
                {cantonName && ` (${cantonName})`}
              </LocationBadge>
            )}

            {/* Category Selector */}
            {!readOnly && (
              <CategorySelectorWrapper ref={wrapperRef}>
                <CategoryTrigger
                  onClick={() => setIsPanelOpen((prev) => !prev)}
                  type="button"
                >
                  <ItemIcon $bgColor={selectedIconBg}>
                    <Icon name={ICON_MAP[selectedCategory]} size="small" />
                  </ItemIcon>
                  <ItemTextContainer>
                    <ItemCode $selected={false}>{selectedCategory}</ItemCode>
                    <ItemLabel $selected={false}>
                      {t(`categories.${selectedCategory}`)}
                    </ItemLabel>
                  </ItemTextContainer>
                  <KwhBadge
                    $textColor={selectedBadge.text}
                    $bgColor={selectedBadge.bg}
                  >
                    {formatKwh(selectedConsumption)}
                  </KwhBadge>
                  <TriggerChevron $open={isPanelOpen}>
                    <Icon name="chevron-down" size="small" />
                  </TriggerChevron>
                </CategoryTrigger>

                {isPanelOpen && (
                  <>
                    <PanelOverlay onClick={() => setIsPanelOpen(false)} />
                    <PanelContainer>
                      {/* Residential column */}
                      <PanelColumn>
                        <ColumnHeader $variant="residential">
                          <ColumnHeaderIcon $variant="residential">
                            <Icon name="home" size="small" />
                          </ColumnHeaderIcon>
                          <ColumnHeaderText>
                            <ColumnHeaderTitle $variant="residential">
                              {t('categoryGroups.residential')}
                            </ColumnHeaderTitle>
                            <ColumnHeaderSubtitle>
                              {t('categoryGroups.residentialSubtitle')}
                            </ColumnHeaderSubtitle>
                          </ColumnHeaderText>
                        </ColumnHeader>
                        {RESIDENTIAL_CATEGORIES.map(renderCategoryItem)}
                      </PanelColumn>

                      {/* Separator */}
                      <ColumnSeparator />

                      {/* Commercial column */}
                      <PanelColumn>
                        <ColumnHeader $variant="commercial">
                          <ColumnHeaderIcon $variant="commercial">
                            <Icon name="factory" size="small" />
                          </ColumnHeaderIcon>
                          <ColumnHeaderText>
                            <ColumnHeaderTitle $variant="commercial">
                              {t('categoryGroups.commercial')}
                            </ColumnHeaderTitle>
                            <ColumnHeaderSubtitle>
                              {t('categoryGroups.commercialSubtitle')}
                            </ColumnHeaderSubtitle>
                          </ColumnHeaderText>
                        </ColumnHeader>
                        {COMMERCIAL_CATEGORIES.map(renderCategoryItem)}
                      </PanelColumn>
                    </PanelContainer>
                  </>
                )}
              </CategorySelectorWrapper>
            )}
          </LocationFilters>
        </FilterBar>
      </FilterSection>

      <AnalysisTabButtonMenu
        tabs={electricityTabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
      />

      <ContentContainer>{renderContent()}</ContentContainer>
    </ElectricityContainer>
  )
}
